import React, { useEffect, useRef, useState } from 'react';
import { Plus, Search, Edit, Trash2, X, User, Truck } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface ScheduledDeduction {
  id: string;
  driver_name: string;
  team_id?: string | null;
  lease_or_rent: 'lease' | 'rent' | 'owner' | 'company_driver' | 'contractor' | 'team';
  company?: string;
  contractor_name?: string;
  contractor_address?: string;
  commission_cpm: string;
  unit_number: string;
  truck_weekly: number;
  truck_monthly: number;
  truck_monthly_charge_day?: string;
  physical_weekly: number;
  physical_monthly: number;
  physical_monthly_charge_day?: string;
  trailer: number;
  cargo: number;
  escrow: number;
  fuel_card_fee: number;
  eld_equipment_fee: number;
  eld_weekly: number;
  eld_monthly: number;
  eld_monthly_charge_day?: string;
  prepass_weekly: number;
  prepass_monthly: number;
  prepass_monthly_charge_day?: string;
  occupational_weekly: number;
  occupational_monthly: number;
  occupational_monthly_charge_day?: string;
  highway_registration_weekly: number;
  created_at: string;
}

interface MonthlyDeductionHistory {
  id: string;
  driver_name: string;
  deduction_type: string;
  amount: number;
  charge_day: string;
  charged_date: string;
  statement_id: string | null;
  created_at: string;
}

function DriverSelect({
  value,
  drivers,
  onChange,
}: {
  value: string;
  drivers: any[];
  onChange: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const query = value.trim().toLowerCase();
  const filtered = drivers.filter(d =>
    `${d.first_name} ${d.last_name}`.toLowerCase().includes(query)
  );

  const select = (d: any) => {
    onChange(`${d.first_name} ${d.last_name}`);
    setOpen(false);
    setHighlighted(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlighted(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      if (highlighted >= 0 && highlighted < filtered.length) {
        select(filtered[highlighted]);
      } else {
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlighted(-1);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlighted(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="Search or enter driver name"
        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        required
      />
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {filtered.length > 0 ? (
            filtered.map((d, i) => {
              const name = `${d.first_name} ${d.last_name}`;
              return (
                <button
                  key={d.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => select(d)}
                  onMouseEnter={() => setHighlighted(i)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors border-b border-gray-100 last:border-b-0 ${
                    i === highlighted ? 'bg-blue-50' : 'bg-white'
                  } ${name === value ? 'text-blue-600 font-medium' : 'text-gray-900'}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <User className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="truncate">{name}</span>
                  </span>
                  {d.truck_unit_number && (
                    <span className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
                      <Truck className="w-3.5 h-3.5" />
                      {d.truck_unit_number}
                    </span>
                  )}
                </button>
              );
            })
          ) : (
            <div className="px-3 py-3 text-sm text-gray-500 text-center">No drivers found</div>
          )}
        </div>
      )}
    </div>
  );
}

export function ScheduledDeductionsView() {
  // Render a single "Truck / Physical / ELD ..." cell that shows the active
  // amount (monthly wins over weekly) with a small cadence tag underneath,
  // or a dash when both are zero. Defined inside the component so Vite HMR
  // keeps it in scope across hot reloads.
  const renderCadenceCell = (weekly: number, monthly: number) => {
    const amount = monthly > 0 ? monthly : weekly;
    const cadence = monthly > 0 ? 'monthly' : weekly > 0 ? 'weekly' : null;
    if (!cadence) {
      return (
        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">—</td>
      );
    }
    return (
      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
        <div className="font-medium">${amount.toFixed(2)}</div>
        <div className="text-xs text-gray-500">{cadence}</div>
      </td>
    );
  };

  const toast = useToast();
  const [activeTab, setActiveTab] = useState<'deductions' | 'history'>('deductions');
  const [deductions, setDeductions] = useState<ScheduledDeduction[]>([]);
  const [history, setHistory] = useState<MonthlyDeductionHistory[]>([]);
  const [trucks, setTrucks] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [historySearchTerm, setHistorySearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDeduction, setSelectedDeduction] = useState<ScheduledDeduction | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deductionToDelete, setDeductionToDelete] = useState<ScheduledDeduction | null>(null);

  const [teams, setTeams] = useState<Array<{ id: string; name: string; member_names: string; member_driver_ids: string[] }>>([]);

  const [formData, setFormData] = useState({
    driver_name: '',
    team_id: '',
    lease_or_rent: 'lease' as 'lease' | 'rent' | 'owner' | 'company_driver' | 'contractor' | 'team',
    company: '',
    contractor_name: '',
    contractor_address: '',
    commission_cpm: '',
    unit_number: '',
    truck_weekly: '',
    truck_monthly: '',
    truck_monthly_charge_day: '1st_friday',
    physical_weekly: '',
    physical_monthly: '',
    physical_monthly_charge_day: '1st_friday',
    trailer: '',
    cargo: '',
    escrow: '',
    fuel_card_fee: '',
    eld_equipment_fee: '',
    eld_weekly: '',
    eld_monthly: '',
    eld_monthly_charge_day: '1st_friday',
    prepass_weekly: '',
    prepass_monthly: '',
    prepass_monthly_charge_day: '1st_friday',
    occupational_weekly: '',
    occupational_monthly: '',
    occupational_monthly_charge_day: '1st_friday',
    highway_registration_weekly: '',
  });

  useEffect(() => {
    loadDeductions();
    loadTrucks();
    loadDrivers();
    loadCompanies();
    loadTeams();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab]);

  async function loadHistory() {
    try {
      setHistoryLoading(true);
      const { data, error } = await supabase
        .from('monthly_deduction_history')
        .select('*')
        .order('charged_date', { ascending: false });

      if (error) throw error;

      setHistory(data || []);
    } catch (error) {
      console.error('Error loading monthly deduction history:', error);
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  }

  async function loadDeductions() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('scheduled_deductions')
        .select('*')
        .order('driver_name');

      if (error) throw error;

      setDeductions(data || []);
    } catch (error) {
      console.error('Error loading scheduled deductions:', error);
      toast.error('Failed to load scheduled deductions');
    } finally {
      setLoading(false);
    }
  }

  async function loadTrucks() {
    try {
      const { data, error } = await supabase
        .from('trucks')
        .select('id, unit_number, make, model')
        .order('unit_number');

      if (error) throw error;
      setTrucks(data || []);
    } catch (error) {
      console.error('Error loading trucks:', error);
    }
  }

  async function loadTeams() {
    try {
      // Active teams with their members. Fails gracefully if the team tables
      // don't exist yet (migration not applied).
      const { data, error } = await supabase
        .from('driver_teams')
        .select('id, name, driver_team_members(drivers(id, first_name, last_name))')
        .eq('is_active', true)
        .order('name');
      if (error) { setTeams([]); return; }
      const mapped = (data || []).map((t: any) => {
        const members = (t.driver_team_members || []).map((m: any) => m.drivers).filter(Boolean);
        return {
          id: t.id,
          name: t.name,
          member_names: members.map((d: any) => `${d.first_name} ${d.last_name}`).join(' & '),
          member_driver_ids: members.map((d: any) => d.id),
        };
      });
      setTeams(mapped);
    } catch (error) {
      console.error('Error loading teams:', error);
      setTeams([]);
    }
  }

  async function loadCompanies() {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name')
        .order('name');
      if (error) throw error;
      setCompanies(data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }

  async function loadDrivers() {
    try {
      // Load drivers with their truck assignments via equipment_assignments
      // Exclude only terminated drivers (include active, vacation, etc.)
      const { data: driversData, error: driversError } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, status, company_id, companies!drivers_company_id_fkey(name)')
        .neq('status', 'terminated')
        .order('first_name');

      if (driversError) throw driversError;

      // Load current truck assignments
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('equipment_assignments')
        .select('driver_id, truck_id, trucks(unit_number)')
        .eq('is_current', true);

      if (assignmentsError) throw assignmentsError;

      // Merge driver data with truck assignments
      const driversWithTrucks = (driversData || []).map(driver => {
        const assignment = (assignmentsData || []).find((a: any) => a.driver_id === driver.id);
        const truckNumber = assignment?.trucks ? (assignment.trucks as any).unit_number : null;
        return {
          ...driver,
          truck_unit_number: truckNumber
        };
      });
      
      setDrivers(driversWithTrucks);
    } catch (error) {
      console.error('Error loading drivers:', error);
    }
  }

  const handleDriverChange = (driverName: string) => {
    // Find the selected driver and auto-populate truck number and company
    const selectedDriver = drivers.find(
      d => `${d.first_name} ${d.last_name}` === driverName
    );

    if (!selectedDriver) {
      // Partial typing — don't touch unit/company until a real driver is picked
      setFormData(prev => ({ ...prev, driver_name: driverName }));
      return;
    }

    const companyName = selectedDriver.companies?.name || '';

    if (selectedDriver.truck_unit_number) {
      setFormData(prev => ({
        ...prev,
        driver_name: driverName,
        unit_number: selectedDriver.truck_unit_number,
        company: companyName
      }));
      toast.success(`Truck ${selectedDriver.truck_unit_number} auto-filled`);
    } else {
      setFormData(prev => ({
        ...prev,
        driver_name: driverName,
        unit_number: '',
        company: companyName
      }));
      toast.info('No truck assigned to this driver. Please select manually.');
    }
  };

  const handleTeamChange = (teamId: string) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) {
      setFormData(prev => ({ ...prev, team_id: '', driver_name: '' }));
      return;
    }
    // driver_name stores the combined label so all existing driver_name-keyed
    // rendering/search keeps working; team_id is the real link.
    const label = team.member_names || team.name;
    // Auto-fill the truck from either member's current assignment, if known.
    const memberWithTruck = drivers.find(
      d => team.member_driver_ids.includes(d.id) && d.truck_unit_number
    );
    setFormData(prev => ({
      ...prev,
      team_id: teamId,
      driver_name: label,
      unit_number: memberWithTruck?.truck_unit_number || prev.unit_number,
    }));
    if (memberWithTruck?.truck_unit_number) {
      toast.success(`Truck ${memberWithTruck.truck_unit_number} auto-filled`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.lease_or_rent === 'team' && !formData.team_id) {
      toast.error('Please select a team');
      return;
    }

    if (!formData.driver_name || !formData.unit_number) {
      toast.error('Please fill in driver name and unit number');
      return;
    }

    try {
      const { error } = await supabase
        .from('scheduled_deductions')
        .insert([{
          driver_name: formData.driver_name.trim(),
          team_id: formData.lease_or_rent === 'team' ? (formData.team_id || null) : null,
          lease_or_rent: formData.lease_or_rent,
          company: formData.company || null,
          contractor_name: ['contractor', 'owner', 'lease', 'rent', 'company_driver', 'team'].includes(formData.lease_or_rent) ? (formData.contractor_name.trim() || null) : null,
          contractor_address: ['contractor', 'owner', 'lease', 'rent', 'company_driver', 'team'].includes(formData.lease_or_rent) ? (formData.contractor_address.trim() || null) : null,
          commission_cpm: formData.commission_cpm,
          unit_number: formData.unit_number.trim(),
          truck_weekly: parseFloat(formData.truck_weekly) || 0,
          truck_monthly: parseFloat(formData.truck_monthly) || 0,
          truck_monthly_charge_day: formData.truck_monthly_charge_day,
          physical_weekly: parseFloat(formData.physical_weekly) || 0,
          physical_monthly: parseFloat(formData.physical_monthly) || 0,
          physical_monthly_charge_day: formData.physical_monthly_charge_day,
          trailer: parseFloat(formData.trailer) || 0,
          cargo: parseFloat(formData.cargo) || 0,
          escrow: parseFloat(formData.escrow) || 0,
          fuel_card_fee: parseFloat(formData.fuel_card_fee) || 0,
          eld_equipment_fee: parseFloat(formData.eld_equipment_fee) || 0,
          eld_weekly: parseFloat(formData.eld_weekly) || 0,
          eld_monthly: parseFloat(formData.eld_monthly) || 0,
          eld_monthly_charge_day: formData.eld_monthly_charge_day,
          prepass_monthly: parseFloat(formData.prepass_monthly) || 0,
          prepass_monthly_charge_day: formData.prepass_monthly_charge_day,
          prepass_weekly: parseFloat(formData.prepass_weekly) || 0,
          occupational_weekly: parseFloat(formData.occupational_weekly) || 0,
          occupational_monthly: parseFloat(formData.occupational_monthly) || 0,
          occupational_monthly_charge_day: formData.occupational_monthly_charge_day,
          highway_registration_weekly: parseFloat(formData.highway_registration_weekly) || 0,
        }]);

      if (error) throw error;

      toast.success('Scheduled deduction added successfully!');
      setShowAddModal(false);
      resetForm();
      loadDeductions();
    } catch (error) {
      console.error('Error adding scheduled deduction:', error);
      toast.error('Failed to add scheduled deduction');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDeduction) return;

    try {
      const { error } = await supabase
        .from('scheduled_deductions')
        .update({
          driver_name: formData.driver_name.trim(),
          team_id: formData.lease_or_rent === 'team' ? (formData.team_id || null) : null,
          lease_or_rent: formData.lease_or_rent,
          company: formData.company || null,
          contractor_name: ['contractor', 'owner', 'lease', 'rent', 'company_driver', 'team'].includes(formData.lease_or_rent) ? (formData.contractor_name.trim() || null) : null,
          contractor_address: ['contractor', 'owner', 'lease', 'rent', 'company_driver', 'team'].includes(formData.lease_or_rent) ? (formData.contractor_address.trim() || null) : null,
          commission_cpm: formData.commission_cpm,
          unit_number: formData.unit_number.trim(),
          truck_weekly: parseFloat(formData.truck_weekly) || 0,
          truck_monthly: parseFloat(formData.truck_monthly) || 0,
          truck_monthly_charge_day: formData.truck_monthly_charge_day,
          physical_weekly: parseFloat(formData.physical_weekly) || 0,
          physical_monthly: parseFloat(formData.physical_monthly) || 0,
          physical_monthly_charge_day: formData.physical_monthly_charge_day,
          trailer: parseFloat(formData.trailer) || 0,
          cargo: parseFloat(formData.cargo) || 0,
          escrow: parseFloat(formData.escrow) || 0,
          fuel_card_fee: parseFloat(formData.fuel_card_fee) || 0,
          eld_equipment_fee: parseFloat(formData.eld_equipment_fee) || 0,
          eld_weekly: parseFloat(formData.eld_weekly) || 0,
          eld_monthly: parseFloat(formData.eld_monthly) || 0,
          eld_monthly_charge_day: formData.eld_monthly_charge_day,
          prepass_monthly: parseFloat(formData.prepass_monthly) || 0,
          prepass_monthly_charge_day: formData.prepass_monthly_charge_day,
          prepass_weekly: parseFloat(formData.prepass_weekly) || 0,
          occupational_weekly: parseFloat(formData.occupational_weekly) || 0,
          occupational_monthly: parseFloat(formData.occupational_monthly) || 0,
          occupational_monthly_charge_day: formData.occupational_monthly_charge_day,
          highway_registration_weekly: parseFloat(formData.highway_registration_weekly) || 0,
        })
        .eq('id', selectedDeduction.id);

      if (error) throw error;

      toast.success('Scheduled deduction updated successfully!');
      setShowEditModal(false);
      setSelectedDeduction(null);
      resetForm();
      loadDeductions();
    } catch (error) {
      console.error('Error updating scheduled deduction:', error);
      toast.error('Failed to update scheduled deduction');
    }
  };

  const handleDelete = async () => {
    if (!deductionToDelete) return;

    try {
      const { error } = await supabase
        .from('scheduled_deductions')
        .delete()
        .eq('id', deductionToDelete.id);

      if (error) throw error;

      toast.success('Scheduled deduction deleted successfully!');
      setShowDeleteConfirm(false);
      setDeductionToDelete(null);
      loadDeductions();
    } catch (error) {
      console.error('Error deleting scheduled deduction:', error);
      toast.error('Failed to delete scheduled deduction');
    }
  };

  const resetForm = () => {
    setFormData({
      driver_name: '',
      team_id: '',
      lease_or_rent: 'lease',
      company: '',
      contractor_name: '',
      contractor_address: '',
      commission_cpm: '',
      unit_number: '',
      truck_weekly: '',
      truck_monthly: '',
      truck_monthly_charge_day: '1st_friday',
      physical_weekly: '',
      physical_monthly: '',
      physical_monthly_charge_day: '1st_friday',
      trailer: '',
      cargo: '',
      escrow: '',
      fuel_card_fee: '',
      eld_equipment_fee: '',
      eld_weekly: '',
      eld_monthly: '',
      eld_monthly_charge_day: '1st_friday',
      prepass_weekly: '',
      prepass_monthly: '',
      prepass_monthly_charge_day: '1st_friday',
      occupational_weekly: '',
      occupational_monthly: '',
      occupational_monthly_charge_day: '1st_friday',
      highway_registration_weekly: '',
    });
  };

  const openEditModal = (deduction: ScheduledDeduction) => {
    setSelectedDeduction(deduction);
    setFormData({
      driver_name: deduction.driver_name,
      team_id: deduction.team_id || '',
      lease_or_rent: deduction.lease_or_rent,
      company: deduction.company || '',
      contractor_name: deduction.contractor_name || '',
      contractor_address: deduction.contractor_address || '',
      commission_cpm: deduction.commission_cpm,
      unit_number: deduction.unit_number,
      truck_weekly: deduction.truck_weekly.toString(),
      truck_monthly: deduction.truck_monthly.toString(),
      truck_monthly_charge_day: deduction.truck_monthly_charge_day || '1st_friday',
      physical_weekly: deduction.physical_weekly.toString(),
      physical_monthly: deduction.physical_monthly.toString(),
      physical_monthly_charge_day: deduction.physical_monthly_charge_day || '1st_friday',
      trailer: deduction.trailer.toString(),
      cargo: deduction.cargo.toString(),
      escrow: (deduction.escrow ?? 0).toString(),
      fuel_card_fee: deduction.fuel_card_fee.toString(),
      eld_equipment_fee: (deduction.eld_equipment_fee ?? 0).toString(),
      eld_weekly: deduction.eld_weekly.toString(),
      eld_monthly: deduction.eld_monthly.toString(),
      eld_monthly_charge_day: deduction.eld_monthly_charge_day || '1st_friday',
      prepass_weekly: deduction.prepass_weekly.toString(),
      prepass_monthly: deduction.prepass_monthly.toString(),
      prepass_monthly_charge_day: deduction.prepass_monthly_charge_day || '1st_friday',
      occupational_weekly: deduction.occupational_weekly.toString(),
      occupational_monthly: deduction.occupational_monthly.toString(),
      occupational_monthly_charge_day: deduction.occupational_monthly_charge_day || '1st_friday',
      highway_registration_weekly: deduction.highway_registration_weekly.toString(),
    });
    setShowEditModal(true);
  };

  // Live truck for a deduction row: the driver's CURRENT assignment from
  // equipment_assignments (already merged into `drivers`), so a truck change
  // made in Safety shows here without editing the deduction. Falls back to
  // the unit_number stored on the deduction when no current assignment exists.
  const getLiveTruck = (deduction: ScheduledDeduction): { unit: string; stale: boolean } => {
    let current: string | null = null;
    if (deduction.team_id) {
      const team = teams.find(t => t.id === deduction.team_id);
      const memberWithTruck = team
        ? drivers.find(d => team.member_driver_ids.includes(d.id) && d.truck_unit_number)
        : null;
      current = memberWithTruck?.truck_unit_number || null;
    } else {
      const driver = drivers.find(
        d => `${d.first_name} ${d.last_name}`.toLowerCase() === deduction.driver_name.toLowerCase()
      );
      current = driver?.truck_unit_number || null;
    }
    if (current && current !== deduction.unit_number) {
      return { unit: current, stale: true };
    }
    return { unit: current || deduction.unit_number, stale: false };
  };

  const filteredDeductions = deductions.filter(deduction => {
    const searchLower = searchTerm.toLowerCase();
    return (
      deduction.driver_name.toLowerCase().includes(searchLower) ||
      deduction.unit_number.toLowerCase().includes(searchLower) ||
      getLiveTruck(deduction).unit.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-none border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Scheduled Deductions</h1>
            <p className="text-sm text-gray-500 mt-1">Manage recurring driver deductions</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Scheduled Deduction
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mt-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('deductions')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'deductions'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Scheduled Deductions
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'history'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Monthly Charge History
          </button>
        </div>

        {/* Search Bar */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={activeTab === 'deductions' ? "Search by driver name or unit number..." : "Search by driver name..."}
            value={activeTab === 'deductions' ? searchTerm : historySearchTerm}
            onChange={(e) => activeTab === 'deductions' ? setSearchTerm(e.target.value) : setHistorySearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Deductions Table */}
      {activeTab === 'deductions' && (
        <div className="flex-1 overflow-auto">
          <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lease/Rent</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission CPM</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit #</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Truck Payment</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Insurance Phys.Dem.</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ELD Weekly Fee</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prepass</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Occupational</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trailer</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cargo</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Escrow</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fuel Card</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Highway/Reg</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredDeductions.length === 0 ? (
              <tr>
                <td colSpan={18} className="px-6 py-12 text-center text-gray-500">
                  <Truck className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-lg font-medium">No scheduled deductions found</p>
                  <p className="text-sm mt-1">Add your first scheduled deduction to get started</p>
                </td>
              </tr>
            ) : (
              filteredDeductions.map((deduction) => (
                <tr
                  key={deduction.id}
                  onClick={() => openEditModal(deduction)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">{deduction.driver_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      deduction.lease_or_rent === 'lease' ? 'bg-blue-100 text-blue-800' :
                      deduction.lease_or_rent === 'rent' ? 'bg-purple-100 text-purple-800' :
                      deduction.lease_or_rent === 'owner' ? 'bg-green-100 text-green-800' :
                      deduction.lease_or_rent === 'contractor' ? 'bg-amber-100 text-amber-800' :
                      deduction.lease_or_rent === 'team' ? 'bg-teal-100 text-teal-800' :
                      'bg-orange-100 text-orange-800'
                    }`}>
                      {deduction.lease_or_rent === 'company_driver' ? 'Company Driver' :
                       deduction.lease_or_rent.charAt(0).toUpperCase() + deduction.lease_or_rent.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      deduction.company === 'APOLLO' ? 'bg-indigo-100 text-indigo-800' :
                      deduction.company === 'TESLA' ? 'bg-red-100 text-red-800' :
                      deduction.company === 'TMARK' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {deduction.company || 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{deduction.commission_cpm}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {(() => {
                      const { unit, stale } = getLiveTruck(deduction);
                      return (
                        <div className="flex items-center">
                          <Truck className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <span className="text-sm font-medium text-gray-900">{unit}</span>
                            {stale && (
                              <div className="text-xs text-gray-400 line-through">{deduction.unit_number}</div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  {renderCadenceCell(deduction.truck_weekly, deduction.truck_monthly)}
                  {renderCadenceCell(deduction.physical_weekly, deduction.physical_monthly)}
                  {renderCadenceCell(deduction.eld_weekly, deduction.eld_monthly)}
                  {renderCadenceCell(deduction.prepass_weekly, deduction.prepass_monthly)}
                  {renderCadenceCell(deduction.occupational_weekly, deduction.occupational_monthly)}
                  {renderCadenceCell(deduction.trailer, 0)}
                  {renderCadenceCell(deduction.cargo, 0)}
                  {renderCadenceCell(deduction.escrow ?? 0, 0)}
                  {renderCadenceCell(deduction.fuel_card_fee, 0)}
                  {renderCadenceCell(deduction.highway_registration_weekly, 0)}
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditModal(deduction); }}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeductionToDelete(deduction);
                        setShowDeleteConfirm(true);
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      )}

      {/* History Table */}
      {activeTab === 'history' && (
        <div className="flex-1 overflow-auto">
          {historyLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deduction Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Charge Day</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Charged Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statement ID</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {history.filter(h => {
                  const searchLower = historySearchTerm.toLowerCase();
                  return h.driver_name.toLowerCase().includes(searchLower);
                }).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No monthly charge history found
                    </td>
                  </tr>
                ) : (
                  history.filter(h => {
                    const searchLower = historySearchTerm.toLowerCase();
                    return h.driver_name.toLowerCase().includes(searchLower);
                  }).map((record) => {
                    const formatDeductionType = (type: string) => {
                      const map: { [key: string]: string } = {
                        'truck_monthly': 'Truck Payment Monthly',
                        'physical_monthly': 'Insurance Phys.Dem. Monthly',
                        'occupational_monthly': 'Occupational Monthly'
                      };
                      return map[type] || type;
                    };

                    const formatFriday = (day: string) => {
                      const map: { [key: string]: string } = {
                        '1st_friday': '1st Friday',
                        '2nd_friday': '2nd Friday',
                        '3rd_friday': '3rd Friday',
                        '4th_friday': '4th Friday'
                      };
                      return map[day] || day;
                    };

                    return (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{record.driver_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{formatDeductionType(record.deduction_type)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">${record.amount.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{formatFriday(record.charge_day)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{new Date(record.charged_date).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 font-mono text-xs">
                          {record.statement_id ? record.statement_id.substring(0, 8) + '...' : 'N/A'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Add Scheduled Deduction</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.lease_or_rent === 'team' ? 'Team' : 'Driver Name'} <span className="text-red-500">*</span>
                  </label>
                  {formData.lease_or_rent === 'team' ? (
                    <select
                      value={formData.team_id}
                      onChange={(e) => handleTeamChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select Team</option>
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>{t.member_names || t.name}</option>
                      ))}
                    </select>
                  ) : (
                    <DriverSelect
                      value={formData.driver_name}
                      drivers={drivers}
                      onChange={handleDriverChange}
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lease or Rent <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.lease_or_rent}
                    onChange={(e) => setFormData({ ...formData, lease_or_rent: e.target.value as 'lease' | 'rent' | 'owner' | 'company_driver' | 'contractor' | 'team' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="lease">Lease</option>
                    <option value="rent">Rent</option>
                    <option value="owner">Owner</option>
                    <option value="company_driver">Company Driver</option>
                    <option value="contractor">Contractor</option>
                    <option value="team">Team</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                  <select
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Company</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>

                {['contractor', 'owner', 'lease', 'rent', 'company_driver', 'team'].includes(formData.lease_or_rent) && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {formData.lease_or_rent === 'contractor' ? 'Contractor Name' : 'Pay-To Company Name'}
                      </label>
                      <input
                        type="text"
                        value={formData.contractor_name}
                        onChange={(e) => setFormData({ ...formData, contractor_name: e.target.value })}
                        placeholder="Legal business name (leave blank to pay driver directly)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {formData.lease_or_rent === 'contractor' ? 'Contractor Address' : 'Pay-To Company Address'}
                      </label>
                      <input
                        type="text"
                        value={formData.contractor_address}
                        onChange={(e) => setFormData({ ...formData, contractor_address: e.target.value })}
                        placeholder="Street, City, State ZIP"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Commission CPM</label>
                  <input
                    type="text"
                    value={formData.commission_cpm}
                    onChange={(e) => setFormData({ ...formData, commission_cpm: e.target.value })}
                    placeholder="e.g., 0.65 or 12% for owners"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter as decimal (0.65 for 65¢/mile) or percentage (12% = company keeps 12%, owner keeps 88%)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit #
                  </label>
                  <input
                    type="text"
                    list="trucks-list-add"
                    value={formData.unit_number}
                    onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                    placeholder="Auto-filled from driver assignment"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                  />
                  <datalist id="trucks-list-add">
                    {trucks.map((truck) => (
                      <option key={truck.id} value={truck.unit_number}>
                        {truck.unit_number} - {truck.make} {truck.model}
                      </option>
                    ))}
                  </datalist>
                  <p className="mt-1 text-xs text-gray-500">
                    Automatically filled based on driver's truck assignment
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Truck Payment Weekly</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.truck_weekly}
                    onChange={(e) => setFormData({ ...formData, truck_weekly: e.target.value, truck_monthly: '', truck_monthly_charge_day: '1st_friday' })}
                    disabled={!!(formData.truck_monthly && parseFloat(formData.truck_monthly) > 0)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                  />
                </div>

                <div className={`md:col-span-2 p-4 rounded-lg border ${formData.truck_weekly && parseFloat(formData.truck_weekly) > 0 ? 'bg-gray-50 border-gray-200 opacity-50' : 'bg-blue-50 border-blue-200'}`}>
                  <h4 className="text-sm font-semibold text-blue-900 mb-3">Truck Payment Monthly Deduction</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.truck_monthly}
                        onChange={(e) => setFormData({ ...formData, truck_monthly: e.target.value, truck_weekly: '' })}
                        disabled={!!(formData.truck_weekly && parseFloat(formData.truck_weekly) > 0)}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Charge Day {formData.truck_monthly && parseFloat(formData.truck_monthly) > 0 && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={formData.truck_monthly_charge_day}
                        onChange={(e) => setFormData({ ...formData, truck_monthly_charge_day: e.target.value })}
                        required={!!(formData.truck_monthly && parseFloat(formData.truck_monthly) > 0)}
                        disabled={!(formData.truck_monthly && parseFloat(formData.truck_monthly) > 0) || !!(formData.truck_weekly && parseFloat(formData.truck_weekly) > 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        <option value="1st_friday">1st Friday</option>
                        <option value="2nd_friday">2nd Friday</option>
                        <option value="3rd_friday">3rd Friday</option>
                        <option value="4th_friday">4th Friday</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Phys.Dem. Weekly</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.physical_weekly}
                    onChange={(e) => setFormData({ ...formData, physical_weekly: e.target.value, physical_monthly: '', physical_monthly_charge_day: '1st_friday' })}
                    disabled={!!(formData.physical_monthly && parseFloat(formData.physical_monthly) > 0)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                  />
                </div>

                <div className={`md:col-span-2 p-4 rounded-lg border ${formData.physical_weekly && parseFloat(formData.physical_weekly) > 0 ? 'bg-gray-50 border-gray-200 opacity-50' : 'bg-green-50 border-green-200'}`}>
                  <h4 className="text-sm font-semibold text-green-900 mb-3">Insurance Phys.Dem. Monthly Deduction</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.physical_monthly}
                        onChange={(e) => setFormData({ ...formData, physical_monthly: e.target.value, physical_weekly: '' })}
                        disabled={!!(formData.physical_weekly && parseFloat(formData.physical_weekly) > 0)}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Charge Day {formData.physical_monthly && parseFloat(formData.physical_monthly) > 0 && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={formData.physical_monthly_charge_day}
                        onChange={(e) => setFormData({ ...formData, physical_monthly_charge_day: e.target.value })}
                        required={!!(formData.physical_monthly && parseFloat(formData.physical_monthly) > 0)}
                        disabled={!(formData.physical_monthly && parseFloat(formData.physical_monthly) > 0) || !!(formData.physical_weekly && parseFloat(formData.physical_weekly) > 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        <option value="1st_friday">1st Friday</option>
                        <option value="2nd_friday">2nd Friday</option>
                        <option value="3rd_friday">3rd Friday</option>
                        <option value="4th_friday">4th Friday</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trailer</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.trailer}
                    onChange={(e) => setFormData({ ...formData, trailer: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cargo</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cargo}
                    onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Escrow</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.escrow}
                    onChange={(e) => setFormData({ ...formData, escrow: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fuel Card Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.fuel_card_fee}
                    onChange={(e) => setFormData({ ...formData, fuel_card_fee: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ELD Equipment Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.eld_equipment_fee}
                    onChange={(e) => setFormData({ ...formData, eld_equipment_fee: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ELD Weekly Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.eld_weekly}
                    onChange={(e) => setFormData({ ...formData, eld_weekly: e.target.value, eld_monthly: '', eld_monthly_charge_day: '1st_friday' })}
                    disabled={!!(formData.eld_monthly && parseFloat(formData.eld_monthly) > 0)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                  />
                </div>

                <div className={`md:col-span-2 p-4 rounded-lg border ${formData.eld_weekly && parseFloat(formData.eld_weekly) > 0 ? 'bg-gray-50 border-gray-200 opacity-50' : 'bg-orange-50 border-orange-200'}`}>
                  <h4 className="text-sm font-semibold text-orange-900 mb-3">ELD Monthly Deduction</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.eld_monthly}
                        onChange={(e) => setFormData({ ...formData, eld_monthly: e.target.value, eld_weekly: '' })}
                        disabled={!!(formData.eld_weekly && parseFloat(formData.eld_weekly) > 0)}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Charge Day {formData.eld_monthly && parseFloat(formData.eld_monthly) > 0 && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={formData.eld_monthly_charge_day}
                        onChange={(e) => setFormData({ ...formData, eld_monthly_charge_day: e.target.value })}
                        required={!!(formData.eld_monthly && parseFloat(formData.eld_monthly) > 0)}
                        disabled={!(formData.eld_monthly && parseFloat(formData.eld_monthly) > 0) || !!(formData.eld_weekly && parseFloat(formData.eld_weekly) > 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        <option value="1st_friday">1st Friday</option>
                        <option value="2nd_friday">2nd Friday</option>
                        <option value="3rd_friday">3rd Friday</option>
                        <option value="4th_friday">4th Friday</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Occupational Weekly Pay</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.occupational_weekly}
                    onChange={(e) => setFormData({ ...formData, occupational_weekly: e.target.value, occupational_monthly: '', occupational_monthly_charge_day: '1st_friday' })}
                    disabled={!!(formData.occupational_monthly && parseFloat(formData.occupational_monthly) > 0)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                  />
                </div>

                <div className={`md:col-span-2 p-4 rounded-lg border ${formData.occupational_weekly && parseFloat(formData.occupational_weekly) > 0 ? 'bg-gray-50 border-gray-200 opacity-50' : 'bg-purple-50 border-purple-200'}`}>
                  <h4 className="text-sm font-semibold text-purple-900 mb-3">Occupational Monthly Deduction</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.occupational_monthly}
                        onChange={(e) => setFormData({ ...formData, occupational_monthly: e.target.value, occupational_weekly: '' })}
                        disabled={!!(formData.occupational_weekly && parseFloat(formData.occupational_weekly) > 0)}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Charge Day {formData.occupational_monthly && parseFloat(formData.occupational_monthly) > 0 && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={formData.occupational_monthly_charge_day}
                        onChange={(e) => setFormData({ ...formData, occupational_monthly_charge_day: e.target.value })}
                        required={!!(formData.occupational_monthly && parseFloat(formData.occupational_monthly) > 0)}
                        disabled={!(formData.occupational_monthly && parseFloat(formData.occupational_monthly) > 0) || !!(formData.occupational_weekly && parseFloat(formData.occupational_weekly) > 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        <option value="1st_friday">1st Friday</option>
                        <option value="2nd_friday">2nd Friday</option>
                        <option value="3rd_friday">3rd Friday</option>
                        <option value="4th_friday">4th Friday</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prepass Weekly Pay</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.prepass_weekly}
                    onChange={(e) => setFormData({ ...formData, prepass_weekly: e.target.value, prepass_monthly: '', prepass_monthly_charge_day: '1st_friday' })}
                    disabled={!!(formData.prepass_monthly && parseFloat(formData.prepass_monthly) > 0)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                  />
                </div>

                <div className={`md:col-span-2 p-4 rounded-lg border ${formData.prepass_weekly && parseFloat(formData.prepass_weekly) > 0 ? 'bg-gray-50 border-gray-200 opacity-50' : 'bg-indigo-50 border-indigo-200'}`}>
                  <h4 className="text-sm font-semibold text-indigo-900 mb-3">Prepass Monthly Deduction</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.prepass_monthly}
                        onChange={(e) => setFormData({ ...formData, prepass_monthly: e.target.value, prepass_weekly: '' })}
                        disabled={!!(formData.prepass_weekly && parseFloat(formData.prepass_weekly) > 0)}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Charge Day {formData.prepass_monthly && parseFloat(formData.prepass_monthly) > 0 && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={formData.prepass_monthly_charge_day}
                        onChange={(e) => setFormData({ ...formData, prepass_monthly_charge_day: e.target.value })}
                        required={!!(formData.prepass_monthly && parseFloat(formData.prepass_monthly) > 0)}
                        disabled={!(formData.prepass_monthly && parseFloat(formData.prepass_monthly) > 0) || !!(formData.prepass_weekly && parseFloat(formData.prepass_weekly) > 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        <option value="1st_friday">1st Friday</option>
                        <option value="2nd_friday">2nd Friday</option>
                        <option value="3rd_friday">3rd Friday</option>
                        <option value="4th_friday">4th Friday</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Highway and Registration Weekly</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.highway_registration_weekly}
                    onChange={(e) => setFormData({ ...formData, highway_registration_weekly: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-6 mt-6 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Deduction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal - Similar structure to Add Modal */}
      {showEditModal && selectedDeduction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Edit Scheduled Deduction</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedDeduction(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpdate} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.lease_or_rent === 'team' ? 'Team' : 'Driver Name'} <span className="text-red-500">*</span>
                  </label>
                  {formData.lease_or_rent === 'team' ? (
                    <select
                      value={formData.team_id}
                      onChange={(e) => handleTeamChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select Team</option>
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>{t.member_names || t.name}</option>
                      ))}
                    </select>
                  ) : (
                    <DriverSelect
                      value={formData.driver_name}
                      drivers={drivers}
                      onChange={handleDriverChange}
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lease or Rent <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.lease_or_rent}
                    onChange={(e) => setFormData({ ...formData, lease_or_rent: e.target.value as 'lease' | 'rent' | 'owner' | 'company_driver' | 'contractor' | 'team' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="lease">Lease</option>
                    <option value="rent">Rent</option>
                    <option value="owner">Owner</option>
                    <option value="company_driver">Company Driver</option>
                    <option value="contractor">Contractor</option>
                    <option value="team">Team</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                  <select
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Company</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>

                {['contractor', 'owner', 'lease', 'rent', 'company_driver', 'team'].includes(formData.lease_or_rent) && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {formData.lease_or_rent === 'contractor' ? 'Contractor Name' : 'Pay-To Company Name'}
                      </label>
                      <input
                        type="text"
                        value={formData.contractor_name}
                        onChange={(e) => setFormData({ ...formData, contractor_name: e.target.value })}
                        placeholder="Legal business name (leave blank to pay driver directly)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {formData.lease_or_rent === 'contractor' ? 'Contractor Address' : 'Pay-To Company Address'}
                      </label>
                      <input
                        type="text"
                        value={formData.contractor_address}
                        onChange={(e) => setFormData({ ...formData, contractor_address: e.target.value })}
                        placeholder="Street, City, State ZIP"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Commission CPM</label>
                  <input
                    type="text"
                    value={formData.commission_cpm}
                    onChange={(e) => setFormData({ ...formData, commission_cpm: e.target.value })}
                    placeholder="e.g., 0.65 or 12% for owners"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter as decimal (0.65 for 65¢/mile) or percentage (12% = company keeps 12%, owner keeps 88%)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit #
                  </label>
                  <input
                    type="text"
                    list="trucks-list-edit"
                    value={formData.unit_number}
                    onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                    placeholder="Auto-filled from driver assignment"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                  />
                  <datalist id="trucks-list-edit">
                    {trucks.map((truck) => (
                      <option key={truck.id} value={truck.unit_number}>
                        {truck.unit_number} - {truck.make} {truck.model}
                      </option>
                    ))}
                  </datalist>
                  <p className="mt-1 text-xs text-gray-500">
                    Automatically filled based on driver's truck assignment
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Truck Payment Weekly</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.truck_weekly}
                    onChange={(e) => setFormData({ ...formData, truck_weekly: e.target.value, truck_monthly: '', truck_monthly_charge_day: '1st_friday' })}
                    disabled={!!(formData.truck_monthly && parseFloat(formData.truck_monthly) > 0)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                  />
                </div>

                <div className={`md:col-span-2 p-4 rounded-lg border ${formData.truck_weekly && parseFloat(formData.truck_weekly) > 0 ? 'bg-gray-50 border-gray-200 opacity-50' : 'bg-blue-50 border-blue-200'}`}>
                  <h4 className="text-sm font-semibold text-blue-900 mb-3">Truck Payment Monthly Deduction</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.truck_monthly}
                        onChange={(e) => setFormData({ ...formData, truck_monthly: e.target.value, truck_weekly: '' })}
                        disabled={!!(formData.truck_weekly && parseFloat(formData.truck_weekly) > 0)}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Charge Day {formData.truck_monthly && parseFloat(formData.truck_monthly) > 0 && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={formData.truck_monthly_charge_day}
                        onChange={(e) => setFormData({ ...formData, truck_monthly_charge_day: e.target.value })}
                        required={!!(formData.truck_monthly && parseFloat(formData.truck_monthly) > 0)}
                        disabled={!(formData.truck_monthly && parseFloat(formData.truck_monthly) > 0) || !!(formData.truck_weekly && parseFloat(formData.truck_weekly) > 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        <option value="1st_friday">1st Friday</option>
                        <option value="2nd_friday">2nd Friday</option>
                        <option value="3rd_friday">3rd Friday</option>
                        <option value="4th_friday">4th Friday</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Phys.Dem. Weekly</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.physical_weekly}
                    onChange={(e) => setFormData({ ...formData, physical_weekly: e.target.value, physical_monthly: '', physical_monthly_charge_day: '1st_friday' })}
                    disabled={!!(formData.physical_monthly && parseFloat(formData.physical_monthly) > 0)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                  />
                </div>

                <div className={`md:col-span-2 p-4 rounded-lg border ${formData.physical_weekly && parseFloat(formData.physical_weekly) > 0 ? 'bg-gray-50 border-gray-200 opacity-50' : 'bg-green-50 border-green-200'}`}>
                  <h4 className="text-sm font-semibold text-green-900 mb-3">Insurance Phys.Dem. Monthly Deduction</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.physical_monthly}
                        onChange={(e) => setFormData({ ...formData, physical_monthly: e.target.value, physical_weekly: '' })}
                        disabled={!!(formData.physical_weekly && parseFloat(formData.physical_weekly) > 0)}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Charge Day {formData.physical_monthly && parseFloat(formData.physical_monthly) > 0 && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={formData.physical_monthly_charge_day}
                        onChange={(e) => setFormData({ ...formData, physical_monthly_charge_day: e.target.value })}
                        required={!!(formData.physical_monthly && parseFloat(formData.physical_monthly) > 0)}
                        disabled={!(formData.physical_monthly && parseFloat(formData.physical_monthly) > 0) || !!(formData.physical_weekly && parseFloat(formData.physical_weekly) > 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        <option value="1st_friday">1st Friday</option>
                        <option value="2nd_friday">2nd Friday</option>
                        <option value="3rd_friday">3rd Friday</option>
                        <option value="4th_friday">4th Friday</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trailer</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.trailer}
                    onChange={(e) => setFormData({ ...formData, trailer: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cargo</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cargo}
                    onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Escrow</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.escrow}
                    onChange={(e) => setFormData({ ...formData, escrow: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fuel Card Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.fuel_card_fee}
                    onChange={(e) => setFormData({ ...formData, fuel_card_fee: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ELD Equipment Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.eld_equipment_fee}
                    onChange={(e) => setFormData({ ...formData, eld_equipment_fee: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ELD Weekly Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.eld_weekly}
                    onChange={(e) => setFormData({ ...formData, eld_weekly: e.target.value, eld_monthly: '', eld_monthly_charge_day: '1st_friday' })}
                    disabled={!!(formData.eld_monthly && parseFloat(formData.eld_monthly) > 0)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                  />
                </div>

                <div className={`md:col-span-2 p-4 rounded-lg border ${formData.eld_weekly && parseFloat(formData.eld_weekly) > 0 ? 'bg-gray-50 border-gray-200 opacity-50' : 'bg-orange-50 border-orange-200'}`}>
                  <h4 className="text-sm font-semibold text-orange-900 mb-3">ELD Monthly Deduction</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.eld_monthly}
                        onChange={(e) => setFormData({ ...formData, eld_monthly: e.target.value, eld_weekly: '' })}
                        disabled={!!(formData.eld_weekly && parseFloat(formData.eld_weekly) > 0)}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Charge Day {formData.eld_monthly && parseFloat(formData.eld_monthly) > 0 && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={formData.eld_monthly_charge_day}
                        onChange={(e) => setFormData({ ...formData, eld_monthly_charge_day: e.target.value })}
                        required={!!(formData.eld_monthly && parseFloat(formData.eld_monthly) > 0)}
                        disabled={!(formData.eld_monthly && parseFloat(formData.eld_monthly) > 0) || !!(formData.eld_weekly && parseFloat(formData.eld_weekly) > 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        <option value="1st_friday">1st Friday</option>
                        <option value="2nd_friday">2nd Friday</option>
                        <option value="3rd_friday">3rd Friday</option>
                        <option value="4th_friday">4th Friday</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Occupational Weekly Pay</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.occupational_weekly}
                    onChange={(e) => setFormData({ ...formData, occupational_weekly: e.target.value, occupational_monthly: '', occupational_monthly_charge_day: '1st_friday' })}
                    disabled={!!(formData.occupational_monthly && parseFloat(formData.occupational_monthly) > 0)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                  />
                </div>

                <div className={`md:col-span-2 p-4 rounded-lg border ${formData.occupational_weekly && parseFloat(formData.occupational_weekly) > 0 ? 'bg-gray-50 border-gray-200 opacity-50' : 'bg-purple-50 border-purple-200'}`}>
                  <h4 className="text-sm font-semibold text-purple-900 mb-3">Occupational Monthly Deduction</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.occupational_monthly}
                        onChange={(e) => setFormData({ ...formData, occupational_monthly: e.target.value, occupational_weekly: '' })}
                        disabled={!!(formData.occupational_weekly && parseFloat(formData.occupational_weekly) > 0)}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Charge Day {formData.occupational_monthly && parseFloat(formData.occupational_monthly) > 0 && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={formData.occupational_monthly_charge_day}
                        onChange={(e) => setFormData({ ...formData, occupational_monthly_charge_day: e.target.value })}
                        required={!!(formData.occupational_monthly && parseFloat(formData.occupational_monthly) > 0)}
                        disabled={!(formData.occupational_monthly && parseFloat(formData.occupational_monthly) > 0) || !!(formData.occupational_weekly && parseFloat(formData.occupational_weekly) > 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        <option value="1st_friday">1st Friday</option>
                        <option value="2nd_friday">2nd Friday</option>
                        <option value="3rd_friday">3rd Friday</option>
                        <option value="4th_friday">4th Friday</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prepass Weekly Pay</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.prepass_weekly}
                    onChange={(e) => setFormData({ ...formData, prepass_weekly: e.target.value, prepass_monthly: '', prepass_monthly_charge_day: '1st_friday' })}
                    disabled={!!(formData.prepass_monthly && parseFloat(formData.prepass_monthly) > 0)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                  />
                </div>

                <div className={`md:col-span-2 p-4 rounded-lg border ${formData.prepass_weekly && parseFloat(formData.prepass_weekly) > 0 ? 'bg-gray-50 border-gray-200 opacity-50' : 'bg-indigo-50 border-indigo-200'}`}>
                  <h4 className="text-sm font-semibold text-indigo-900 mb-3">Prepass Monthly Deduction</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.prepass_monthly}
                        onChange={(e) => setFormData({ ...formData, prepass_monthly: e.target.value, prepass_weekly: '' })}
                        disabled={!!(formData.prepass_weekly && parseFloat(formData.prepass_weekly) > 0)}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Charge Day {formData.prepass_monthly && parseFloat(formData.prepass_monthly) > 0 && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={formData.prepass_monthly_charge_day}
                        onChange={(e) => setFormData({ ...formData, prepass_monthly_charge_day: e.target.value })}
                        required={!!(formData.prepass_monthly && parseFloat(formData.prepass_monthly) > 0)}
                        disabled={!(formData.prepass_monthly && parseFloat(formData.prepass_monthly) > 0) || !!(formData.prepass_weekly && parseFloat(formData.prepass_weekly) > 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        <option value="1st_friday">1st Friday</option>
                        <option value="2nd_friday">2nd Friday</option>
                        <option value="3rd_friday">3rd Friday</option>
                        <option value="4th_friday">4th Friday</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Highway and Registration Weekly</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.highway_registration_weekly}
                    onChange={(e) => setFormData({ ...formData, highway_registration_weekly: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-6 mt-6 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedDeduction(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Update Deduction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deductionToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Delete Scheduled Deduction</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the scheduled deduction for{' '}
              <span className="font-semibold">{deductionToDelete.driver_name}</span>?
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeductionToDelete(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
